"use client";
import React from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import ReactMarkdown from "react-markdown";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";
import { motion } from "framer-motion";

interface ContentTextProps {
    title: string;
    text: string;
}

interface Button {
    id: string;
    url: string;
    text: string;
    type: string;
    newTab: boolean;
}

interface Picture {
    data: {
        id: string;
        attributes: {
            url: string;
            name: string;
            alternativeText: string;
        };
    };
}

interface ContentProps {
    data: {
        id: number;
        title: string;
        subtitle: string;
        picture: Picture;
        isReversed: boolean;
        contentTexts: ContentTextProps[];
        button: Button[];
        isLive: boolean;
        useH1Tag?: boolean;
        variant?: "home" | "patent";
        bgColor?: string;
    };
}


// Main Component
export default function LayoutAbout1({ data }: ContentProps) {
    const pathname = usePathname();
    const imgUrl = getStrapiMedia(data.picture.data.attributes.url);
    const bgColor = data.bgColor || "#bd9d79";

    if (!data.isLive) return null;

    return (
        <>
            <section
                className="relative w-full overflow-hidden max-w-[1450px] mx-auto "
                id="content-section"
                style={{ backgroundColor: bgColor }}
            >
                <div className="relative w-full  h-[400px]">
                    <Image
                        src={imgUrl || "/placeholder.jpg"}
                        alt={data.picture?.data?.attributes?.alternativeText || "Content image"}
                        fill
                        className="object-cover w-full h-full object-left"
                        sizes="100vw"
                        priority
                    />
                    {/* Optional dark gradient for better readability */}
                    <div className="absolute inset-0 bg-gradient-to-l from-black/40 via-black/20 to-transparent"></div>

                    {/* Content Box */}
                    <div className="absolute inset-0 flex items-start justify-end px-4 sm:px-8 lg:px-16 py-6 sm:py-8 lg:py-12">
                        <div className="p-4 sm:p-6 max-w-full sm:max-w-[600px] lg:max-w-[700px] text-left text-[var(--secondary)]">

                            {/* Title */}
                            {data.title && (
                                <p className="mb-3 font-medium text-sm sm:text-base md:text-lg lg:text-xl leading-snug text-black">
                                    {data.title}
                                </p>
                            )}

                            {/* Subtitle */}
                            {data.subtitle && (
                                <p className="mb-3 text-md sm:text-sm md:text-base lg:text-xl  font-light text-black">
                                    {data.subtitle}
                                </p>
                            )}

                            {/* Bullet Points */}
                            {data.contentTexts?.length > 0 && (
                                <ul className="list-disc list-inside space-y-1 text-sm  sm:text-sm md:text-base lg:text-lg text-black pl-4">
                                    {data.contentTexts[0].text
                                        ?.split("\n")
                                        .filter(Boolean)
                                        .map((line, i) => (
                                            <li key={i}>{line.trim()}</li>
                                        ))}
                                </ul>
                            )}

                            {/* CTA Button */}
                            {data.button?.length > 0 && (
                                <Link
                                    href={data.button[0].url}
                                    target={data.button[0].newTab ? "_blank" : "_self"}
                                    aria-label={`Navigate to ${data.contentTexts[0]?.title || "this page"}`}
                                    className="inline-block mt-6 px-6 py-3 rounded-full bg-th-btncol-1 hover:bg-th-btncol-2 text-white font-semibold text-sm shadow transition-all duration-200"
                                >
                                    {data.button[0].text}
                                </Link>
                            )}
                        </div>
                    </div>

                </div>
            </section>
        </>
    );
}
